import {
  AreaChart, Area, BarChart, Bar, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
} from "recharts";
import { fmt } from "@/lib/api";

const tooltipStyle = {
  backgroundColor: "#0c1428",
  border: "1px solid rgba(255,255,255,0.1)",
  borderRadius: "8px",
  fontSize: "12px",
  color: "#e6edf7",
};

const axisProps = { stroke: "#5b6b8c", fontSize: 11, tickLine: false, axisLine: false };

export function TrendChart({ data = [], keys = [{ key: "impressions", color: "#3b82f6", name: "Impressions" }], height = 300 }) {
  return (
    <ResponsiveContainer width="100%" height={height}>
      <AreaChart data={data} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
        <defs>
          {keys.map((k) => (
            <linearGradient key={k.key} id={`grad-${k.key}`} x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor={k.color} stopOpacity={0.35} />
              <stop offset="100%" stopColor={k.color} stopOpacity={0} />
            </linearGradient>
          ))}
        </defs>
        <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" vertical={false} />
        <XAxis dataKey="date" {...axisProps} tickFormatter={(d) => String(d).slice(5)} />
        <YAxis {...axisProps} tickFormatter={(v) => (v >= 1000 ? `${(v / 1000).toFixed(0)}k` : v)} width={44} />
        <Tooltip contentStyle={tooltipStyle} formatter={(v) => fmt(v)} />
        {keys.map((k) => (
          <Area key={k.key} type="monotone" dataKey={k.key} name={k.name} stroke={k.color} strokeWidth={2}
            fill={`url(#grad-${k.key})`} dot={false} activeDot={{ r: 4 }} />
        ))}
      </AreaChart>
    </ResponsiveContainer>
  );
}

export function MetricBarChart({ data = [], xKey = "name", barKey = "impressions", color = "#06b6d4", height = 280 }) {
  return (
    <ResponsiveContainer width="100%" height={height}>
      <BarChart data={data} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
        <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" vertical={false} />
        <XAxis dataKey={xKey} {...axisProps} />
        <YAxis {...axisProps} tickFormatter={(v) => (v >= 1000 ? `${(v / 1000).toFixed(0)}k` : v)} width={44} />
        <Tooltip contentStyle={tooltipStyle} formatter={(v) => fmt(v)} />
        <Bar dataKey={barKey} fill={color} radius={[4, 4, 0, 0]} maxBarSize={42} />
      </BarChart>
    </ResponsiveContainer>
  );
}

const FUNNEL_COLORS = ["#3b82f6", "#2563eb", "#0ea5e9", "#06b6d4", "#22d3ee"];

export function VideoFunnel({ video = {}, height = 260 }) {
  const data = [
    { stage: "Starts", value: video.starts || 0 },
    { stage: "25%", value: video.v25 || 0 },
    { stage: "50%", value: video.v50 || 0 },
    { stage: "75%", value: video.v75 || 0 },
    { stage: "100%", value: video.complete || 0 },
  ];
  return (
    <ResponsiveContainer width="100%" height={height}>
      <BarChart data={data} layout="vertical" margin={{ top: 5, right: 20, left: 10, bottom: 5 }}>
        <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" horizontal={false} />
        <XAxis type="number" {...axisProps} tickFormatter={(v) => (v >= 1000 ? `${(v / 1000).toFixed(0)}k` : v)} />
        <YAxis type="category" dataKey="stage" {...axisProps} width={50} />
        <Tooltip contentStyle={tooltipStyle} formatter={(v) => fmt(v)} />
        <Bar dataKey="value" radius={[0, 4, 4, 0]} maxBarSize={22}>
          {data.map((_, i) => <Cell key={i} fill={FUNNEL_COLORS[i]} />)}
        </Bar>
      </BarChart>
    </ResponsiveContainer>
  );
}

export function DonutChart({ data = [], height = 260 }) {
  const COLORS = ["#3b82f6", "#06b6d4", "#22d3ee", "#6366f1", "#0ea5e9"];
  return (
    <ResponsiveContainer width="100%" height={height}>
      <PieChart>
        <Pie data={data} dataKey="value" nameKey="name" innerRadius={58} outerRadius={88} paddingAngle={3} strokeWidth={0}>
          {data.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
        </Pie>
        <Tooltip contentStyle={tooltipStyle} formatter={(v) => fmt(v)} />
      </PieChart>
    </ResponsiveContainer>
  );
}
